import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { PremiumFeature } from '../components/PremiumFeature';
import { PlaidLink } from '../components/PlaidLink';
import { formatCurrency } from '../utils/format';
import { useSwipeBack } from '../hooks/useSwipeBack';
import { useTranslation } from 'react-i18next';

interface PaymentMethod {
  id: string;
  name: string;
  type: string;
  balance?: number;
  created_at: string;
}

interface PlaidAccount {
  id: number;
  item_id: string;
  account_id: string;
  name: string;
  official_name?: string;
  type: string;
  subtype: string;
  mask?: string;
  balance_current: number;
  balance_available: number;
  currency_code: string;
  institution_name: string;
  item_status: string;
  created_at: string;
}

interface PlaidItem {
  id: number;
  item_id: string;
  institution_name: string;
  status: string;
  error_code?: string;
  error_message?: string;
  created_at: string;
  last_successful_sync?: string;
}

export const AccountsScreen: React.FC = () => {
  const { setCurrentPage, settings } = useAppStore();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [manualAccounts, setManualAccounts] = useState<PaymentMethod[]>([]);
  const [plaidAccounts, setPlaidAccounts] = useState<PlaidAccount[]>([]);
  const [plaidItems, setPlaidItems] = useState<PlaidItem[]>([]);
  const [showAddManualModal, setShowAddManualModal] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountBalance, setNewAccountBalance] = useState('');
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectingItemId, setDisconnectingItemId] = useState<string | null>(null);
  const [syncingItemId, setSyncingItemId] = useState<string | null>(null);
  const [editingBalanceId, setEditingBalanceId] = useState<string | null>(null);
  const [editingBalanceValue, setEditingBalanceValue] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState<PaymentMethod | null>(null);
  const [selectedTargetAccount, setSelectedTargetAccount] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const [manualData, plaidData] = await Promise.all([
        apiClient.getPaymentMethods(),
        apiClient.getPlaidAccounts(),
      ]);

      console.log('📊 Manual accounts loaded:', manualData.payment_methods);
      // Проверяем, что баланс загружается
      manualData.payment_methods?.forEach((acc: any) => {
        console.log(`  Account ${acc.name}: balance = ${acc.balance} (type: ${typeof acc.balance})`);
      });
      setManualAccounts(manualData.payment_methods || []);
      setPlaidAccounts(plaidData.accounts || []);
      setPlaidItems(plaidData.items || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddManualAccount = async () => {
    if (!newAccountName.trim()) {
      return;
    }

    try {
      setIsAddingManual(true);
      const balance = parseFloat(newAccountBalance) || 0;
      await apiClient.createPaymentMethod(newAccountName.trim(), 'general', balance);
      setNewAccountName('');
      setNewAccountBalance('');
      setShowAddManualModal(false);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.addError'));
    } finally {
      setIsAddingManual(false);
    }
  };

  const handleEditBalance = async (accountId: string) => {
    console.log('🔄 [handleEditBalance] Called with accountId:', accountId);
    console.log('   Current editingBalanceId:', editingBalanceId);
    console.log('   Manual accounts:', manualAccounts.map(a => ({ id: a.id, name: a.name, balance: a.balance })));
    
    if (editingBalanceId !== accountId) {
      const account = manualAccounts.find(a => a.id === accountId);
      console.log('   Account found:', account);
      console.log('   Account balance:', account?.balance);
      setEditingBalanceId(accountId);
      setEditingBalanceValue(account?.balance?.toString() || '0');
      return;
    }

    try {
      const balance = parseFloat(editingBalanceValue) || 0;
      console.log('   Saving balance:', balance);
      await apiClient.updatePaymentMethodBalance(accountId, balance);
      setEditingBalanceId(null);
      setEditingBalanceValue('');
      await loadAccounts();
      console.log('✅ Balance updated successfully');
    } catch (error: any) {
      console.error('❌ Error updating balance:', error);
      alert(error.message || t('accounts.updateBalanceError'));
    }
  };

  const handleDeleteManualAccount = async (accountId: string) => {
    const account = manualAccounts.find(a => a.id === accountId);
    
    // Не позволяем удалять основной счет
    if (account?.type === 'main') {
      alert(t('accounts.cannotDeleteDefault'));
      return;
    }

    // Если баланс не нулевой - показываем модалку переноса
    const balance = account?.balance || 0;
    if (balance !== 0) {
      setDeletingAccount(account || null);
      setSelectedTargetAccount(null);
      setShowTransferModal(true);
      return;
    }

    // Если баланс 0 - просто удаляем с подтверждением
    if (!confirm(t('accounts.deleteConfirm'))) {
      return;
    }

    try {
      await apiClient.deletePaymentMethod(accountId);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.deleteError'));
    }
  };

  const handleTransferAndDelete = async () => {
    if (!deletingAccount || !selectedTargetAccount) return;
    
    try {
      setIsDeleting(true);
      await apiClient.deletePaymentMethod(deletingAccount.id, selectedTargetAccount);
      await loadAccounts();
      setShowTransferModal(false);
      setDeletingAccount(null);
      setSelectedTargetAccount(null);
    } catch (error: any) {
      alert(error.message || t('accounts.transferDeleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetDefault = async (accountId: string) => {
    try {
      const account = manualAccounts.find(a => a.id === accountId);
      const isCurrentlyDefault = account?.is_default || false;
      await apiClient.setDefaultPaymentMethod(accountId, !isCurrentlyDefault);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.setDefaultError'));
    }
  };

  const handleSyncTransactions = async (itemId: string) => {
    try {
      setSyncingItemId(itemId);
      await apiClient.syncPlaidTransactions(itemId);
      await loadAccounts(); // Перезагружаем для обновления балансов
      alert(t('accounts.syncSuccess'));
    } catch (error: any) {
      alert(error.message || t('accounts.syncError'));
    } finally {
      setSyncingItemId(null);
    }
  };

  const handleDisconnect = async () => {
    if (!disconnectingItemId) return;

    try {
      await apiClient.disconnectPlaidAccount(disconnectingItemId);
      setShowDisconnectModal(false);
      setDisconnectingItemId(null);
      await loadAccounts();
    } catch (error: any) {
      alert(error.message || t('accounts.unlinkError'));
    }
  };

  const handlePlaidSuccess = async () => {
    await loadAccounts();
  };

  useSwipeBack(containerRef, () => setCurrentPage('settings'));

  const currencySymbol = getCurrencySymbol(settings?.currency || 'USD');

  // Группируем банковские счета по учреждениям
  const accountsByInstitution = plaidAccounts.reduce((acc, account) => {
    const institution = account.institution_name;
    if (!acc[institution]) {
      acc[institution] = [];
    }
    acc[institution].push(account);
    return acc;
  }, {} as Record<string, PlaidAccount[]>);

  if (loading) {
    return (
      <div ref={containerRef} className="accounts-screen page">
        <Header title={t('operations.tabs.accounts')} onBack={() => setCurrentPage('settings')} />
        <div className="accounts-loading">
                      <Icon name="credit_card" size={48} color="var(--color-primary)" />
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary)' }}>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="accounts-screen page">
      <Header title={t('operations.tabs.accounts')} onBack={() => setCurrentPage('settings')} />

      <div className="accounts-content">
        {/* Кнопка привязки банка */}
        <div className="section">
          <PremiumFeature featureId="plaid_bank_linking">
            <PlaidLink
              onSuccess={handlePlaidSuccess}
              buttonText={t('accounts.linkBank')}
              className="plaid-link-button"
            />
          </PremiumFeature>
        </div>

        {/* Банковские счета */}
        {plaidAccounts.length > 0 && (
          <div className="section">
            <h3 className="section__title">{t('accounts.bankAccounts')}</h3>
            {Object.entries(accountsByInstitution).map(([institution, accounts]) => {
              const item = plaidItems.find(i => i.institution_name === institution);
              return (
                <Card key={institution} className="bank-institution-card neo-static">
                  <div className="bank-institution-header">
                    <div className="bank-institution-info">
                      <Icon name="building" size={20} color="var(--color-primary)" />
                      <span className="bank-institution-name">{institution}</span>
                      {item.status === 'error' && (
                        <span className="bank-institution-status-error" title={item.error_message || t('accounts.connectionError')}>
                          <Icon name="alert-circle" size={16} color="#FF3B30" />
                        </span>
                      )}
                    </div>
                    {item && (
                      <div className="bank-institution-actions">
                        {item.status === 'error' && item.error_code === 'INVALID_ACCESS_TOKEN' && (
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => {
                              // Переподключить банк - открыть Plaid Link заново
                              window.location.href = '/accounts?reconnect=' + item.item_id;
                            }}
                          >
                            {t('accounts.reconnect')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => handleSyncTransactions(item.item_id)}
                          loading={syncingItemId === item.item_id}
                          disabled={syncingItemId !== null || item.status === 'error'}
                        >
                          <Icon name="refresh" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => {
                            setDisconnectingItemId(item.item_id);
                            setShowDisconnectModal(true);
                          }}
                        >
                          <Icon name="x" size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                  {item.status === 'error' && item.error_message && (
                    <div className="bank-institution-error-message">
                      <Icon name="alert-circle" size={16} color="#FF3B30" />
                      <span>{item.error_message}</span>
                    </div>
                  )}
                  <div className="bank-accounts-list">
                    {accounts.map(account => (
                      <div key={account.account_id} className="bank-account-item">
                        <div className="account-info">
                          <div className="account-name-row">
                            <span className="account-name">{account.name}</span>
                            {account.mask && (
                              <span className="account-mask">•••• {account.mask}</span>
                            )}
                          </div>
                          <span className="account-type">
                            {account.subtype || account.type}
                          </span>
                        </div>
                        <div className="account-balance">
                          <span className="balance-amount">
                            {(() => {
                              // Для кредитных карт баланс должен быть отрицательным (долг)
                              // В Plaid баланс кредитной карты положительный = долг
                              const isCredit = account.type === 'credit' || account.subtype === 'credit card';
                              const balance = isCredit 
                                ? -Math.abs(account.balance_current) // Инвертируем для кредитных карт
                                : account.balance_current; // Для депозитных счетов оставляем как есть
                              return formatCurrency(balance, account.currency_code || 'USD');
                            })()}
                          </span>
                          {account.balance_available !== account.balance_current && (
                            <span className="balance-available">
                              {(() => {
                                const isCredit = account.type === 'credit' || account.subtype === 'credit card';
                                const balance = isCredit 
                                  ? -Math.abs(account.balance_available)
                                  : account.balance_available;
                                return formatCurrency(balance, account.currency_code || 'USD');
                              })()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Ручные счета */}
        <div className="section">
          <div className="section-header">
            <h3 className="section__title">{t('accounts.manualAccounts')}</h3>
            <Button
              variant="ghost"
              size="small"
              onClick={() => setShowAddManualModal(true)}
            >
                      <Icon name="add" size={16} />
            </Button>
          </div>
          {manualAccounts.length === 0 ? (
            <Card>
              <div className="empty-state">
                      <Icon name="credit_card" size={48} color="var(--color-text-secondary)" />
                <p>{t('accounts.noManualAccounts')}</p>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => setShowAddManualModal(true)}
                >
                  {t('accounts.addAccount')}
                </Button>
              </div>
            </Card>
          ) : (
            <div className="manual-accounts-list">
              {manualAccounts.map(account => (
                <Card key={account.id} className="manual-account-item neo-static" padding="small">
                  <div className="manual-account-content">
                    <div className="account-info">
                      <span className="account-name">{account.name}</span>
                      <span className="account-type">{account.type}</span>
                    </div>
                    <div className="account-balance-row">
                      {editingBalanceId === account.id ? (
                        <div className="balance-edit-form">
                          <input
                            type="number"
                            step="0.01"
                            value={editingBalanceValue}
                            onChange={(e) => setEditingBalanceValue(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleEditBalance(account.id);
                              }
                              if (e.key === 'Escape') {
                                setEditingBalanceId(null);
                                setEditingBalanceValue('');
                              }
                            }}
                            className="balance-input"
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => handleEditBalance(account.id)}
                          >
                            <Icon name="check" size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => {
                              setEditingBalanceId(null);
                              setEditingBalanceValue('');
                            }}
                          >
                            <Icon name="x" size={16} />
                          </Button>
                        </div>
                      ) : (
                        <div className="account-balance-display" onClick={() => handleEditBalance(account.id)} title={t('accounts.editBalanceHint')}>
                          <span className="balance-amount">
                            {formatCurrency(account.balance ?? 0, settings?.currency || 'USD')}
                          </span>
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditBalance(account.id);
                            }}
                            title={t('accounts.editBalance')}
                          >
                            <Icon name="edit" size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                        <div className="account-actions">
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => handleSetDefault(account.id)}
                            title={account.is_default ? t('accounts.unsetDefault') : t('accounts.setDefault')}
                            className={account.is_default ? "default-account-btn default-account-btn--active" : "default-account-btn"}
                          >
                            <Icon name={account.is_default ? "star" : "star"} size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() => handleDeleteManualAccount(account.id)}
                            title={t('accounts.deleteTooltip')}
                          >
                            <Icon name="trash" size={16} />
                          </Button>
                        </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно добавления ручного счета */}
      <Modal
        isOpen={showAddManualModal}
        onClose={() => {
          setShowAddManualModal(false);
          setNewAccountName('');
        }}
        title={t('accounts.addAccount')}
      >
        <div className="add-manual-account-form">
          <input
            type="text"
            placeholder={t('accounts.namePlaceholder')}
            value={newAccountName}
            onChange={(e) => setNewAccountName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddManualAccount();
              }
            }}
            autoFocus
          />
          <input
            type="number"
            step="0.01"
            placeholder={t('accounts.balancePlaceholder')}
            value={newAccountBalance}
            onChange={(e) => setNewAccountBalance(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddManualAccount();
              }
            }}
          />
          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddManualModal(false);
                setNewAccountName('');
                setNewAccountBalance('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAddManualAccount}
              disabled={!newAccountName.trim()}
              loading={isAddingManual}
            >
              {t('accounts.addAccount')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Модальное окно отключения банка */}
      <Modal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setDisconnectingItemId(null);
        }}
        title={t('accounts.disconnectTitle')}
      >
        <div className="disconnect-modal-content">
          <p>{t('accounts.disconnectConfirm')}</p>
          <p className="disconnect-warning">
            {t('accounts.disconnectNote')}
          </p>
          <div className="form-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDisconnectModal(false);
                setDisconnectingItemId(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDisconnect}
            >
              {t('accounts.disconnectConfirmButton')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Модальное окно переноса средств при удалении счёта */}
      <Modal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setDeletingAccount(null);
          setSelectedTargetAccount(null);
        }}
        title={t('accounts.transferTitle')}
      >
        <div className="transfer-modal-content">
          {deletingAccount && (
            <>
              <div className="transfer-info">
                <div className="transfer-info-row">
                  <Icon name="credit_card" size={20} />
                  <div className="transfer-info-content">
                    <span className="transfer-info-label">{t('accounts.transferSource')}</span>
                    <span className="transfer-info-value">{deletingAccount.name}</span>
                  </div>
                </div>
                <div className="transfer-info-row">
                  <Icon 
                    name={(deletingAccount.balance || 0) >= 0 ? "arrow_upward" : "arrow_downward"} 
                    size={20} 
                    color={(deletingAccount.balance || 0) >= 0 ? 'var(--color-success)' : 'var(--color-danger)'} 
                  />
                  <div className="transfer-info-content">
                    <span className="transfer-info-label">{t('accounts.transferBalance')}</span>
                    <span 
                      className="transfer-info-value" 
                      style={{ color: (deletingAccount.balance || 0) >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}
                    >
                      {formatCurrency(deletingAccount.balance || 0, settings?.currency || 'RUB')}
                    </span>
                  </div>
                </div>
              </div>

              <p className="transfer-description">
                {t('accounts.transferDescription')}
              </p>

              <div className="transfer-accounts-list">
                {manualAccounts
                  .filter(a => a.id !== deletingAccount.id && a.type !== 'main')
                  .map(account => (
                    <div
                      key={account.id}
                      className={`transfer-account-item ${selectedTargetAccount === account.id ? 'transfer-account-item--selected' : ''}`}
                      onClick={() => setSelectedTargetAccount(account.id)}
                    >
                      <div className="transfer-account-info">
                        <Icon name="credit_card" size={18} />
                        <span className="transfer-account-name">{account.name}</span>
                      </div>
                      <span className="transfer-account-balance">
                        {formatCurrency(account.balance || 0, settings?.currency || 'RUB')}
                      </span>
                      <div className={`transfer-radio ${selectedTargetAccount === account.id ? 'transfer-radio--selected' : ''}`}>
                        {selectedTargetAccount === account.id && <div className="transfer-radio-inner" />}
                      </div>
                    </div>
                  ))}
                {manualAccounts.filter(a => a.id !== deletingAccount.id && a.type !== 'main').length === 0 && (
                  <p className="transfer-no-accounts">
                    {t('accounts.transferNoTargets')}
                  </p>
                )}
              </div>

              <div className="form-actions">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowTransferModal(false);
                    setDeletingAccount(null);
                    setSelectedTargetAccount(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onClick={handleTransferAndDelete}
                  disabled={!selectedTargetAccount || isDeleting}
                  loading={isDeleting}
                >
                  <Icon name="swap_horiz" size={16} />
                  {t('accounts.transferAndDelete')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

